import { useEffect, useState } from 'react'

import { isDiscountDateActive } from 'src/shared/discountShop'

type DiscountDateValue = Date | string | number | null | undefined

const MAX_TIMEOUT_MS = 2147483647

const parseTimestamp = (value: DiscountDateValue): number | undefined => {
  if (value == null || value === '') return undefined
  if (value instanceof Date) {
    const timestamp = value.getTime()
    return Number.isFinite(timestamp) ? timestamp : undefined
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined

  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : undefined
}

const getNextBoundary = (
  now: number,
  dateStart?: DiscountDateValue,
  dateEnd?: DiscountDateValue,
) => [parseTimestamp(dateStart), parseTimestamp(dateEnd)]
  .filter((timestamp): timestamp is number =>
    typeof timestamp === 'number' && timestamp > now,
  )
  .sort((a, b) => a - b)[0]

export const useDiscountDateActive = (
  dateEnd?: DiscountDateValue,
  dateStart?: DiscountDateValue,
) => {
  const [now, setNow] = useState<number | null>(null)

  useEffect(() => {
    if (!dateStart && !dateEnd) {
      setNow(null)
      return
    }

    let timeout: ReturnType<typeof setTimeout> | undefined

    const schedule = () => {
      const current = Date.now()
      setNow(current)

      const nextBoundary = getNextBoundary(current, dateStart, dateEnd)
      if (nextBoundary === undefined) return

      timeout = setTimeout(
        schedule,
        Math.min(MAX_TIMEOUT_MS, Math.max(0, nextBoundary - current) + 100),
      )
    }

    schedule()

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [dateStart, dateEnd])

  if (now == null) return true

  return isDiscountDateActive({ dateStart, dateEnd, now })
}
